import type { Control,UseFormGetValues } from "react-hook-form"

import { RadioGroup,RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Card,CardContent,CardDescription,CardHeader,CardTitle } from "@/components/ui/card"
import {
FormControl,
FormDescription,
FormField,
FormItem,
FormLabel,
FormMessage,
} from "@/components/ui/form"
import { NumericInput } from "@/components/ui/numeric-input"
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from "@/components/ui/select"
import type { PricingScheme,UnitOfMeasurement } from "@/types"

import type { SaleConfigFormValues } from "./sale-config-utils"

interface PricingSectionProps {
  control: Control<SaleConfigFormValues>
  pricingMode: "simple" | "tiered"
  onPricingModeChange: (mode: "simple" | "tiered") => void
  simplePriceDisplay: string
  onSimplePriceDisplayChange: (value: string) => void
  filteredPricingSchemes: PricingScheme[]
  uoms: UnitOfMeasurement[]
  formGetValues: UseFormGetValues<SaleConfigFormValues>
}

export const PricingSection = ({
  control,
  pricingMode,
  onPricingModeChange,
  simplePriceDisplay,
  onSimplePriceDisplayChange,
  filteredPricingSchemes,
  uoms,
  formGetValues,
}: PricingSectionProps) => {
  return (
    <Card>
      <CardHeader className="px-4 py-1.5">
        <CardTitle className="text-base">Pricing</CardTitle>
        <CardDescription>Choose between simple flat pricing or advanced tiered pricing.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-4 pb-4">
        <div className="space-y-3">
          <Label>Pricing Type</Label>
          <RadioGroup value={pricingMode} onValueChange={(value) => onPricingModeChange(value as "simple" | "tiered")}>
            <Label htmlFor="simple" className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-accent">
              <RadioGroupItem value="simple" id="simple" />
              <div className="flex-1">
                <div className="font-medium">Simple (Flat Price)</div>
                <div className="text-xs text-muted-foreground">Single fixed price - best for pre-packaged items</div>
              </div>
            </Label>
            <Label htmlFor="tiered" className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-accent">
              <RadioGroupItem value="tiered" id="tiered" />
              <div className="flex-1">
                <div className="font-medium">Tiered Pricing</div>
                <div className="text-xs text-muted-foreground">Multiple price tiers - best for bulk or quantity discounts</div>
              </div>
            </Label>
          </RadioGroup>
        </div>

        {pricingMode === "simple" ? (
          <FormField
            control={control}
            name="simplePriceCents"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="gap-0.5">Price <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <div className="relative">
                    <NumericInput
                      id={field.name}
                      value={simplePriceDisplay}
                      onChange={(val) => {
                        onSimplePriceDisplayChange(val)
                        if (val === "") {
                          field.onChange(undefined)
                        } else {
                          const dollars = parseFloat(val)
                          if (!Number.isNaN(dollars)) {
                            field.onChange(Math.round(dollars * 100))
                          }
                        }
                      }}
                      onBlur={() => {
                        if (simplePriceDisplay && !Number.isNaN(parseFloat(simplePriceDisplay))) {
                          onSimplePriceDisplayChange(parseFloat(simplePriceDisplay).toFixed(2))
                        }
                      }}
                      allowDecimals={true}
                      prefix="$"
                      placeholder="25.00"
                      className="pl-9"
                    />
                  </div>
                </FormControl>
                <FormDescription>Enter the price in dollars</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={control}
            name="pricingSchemeId"
            render={({ field }) => {
              const selectedScheme = filteredPricingSchemes.find((s) => s.id === field.value)
              return (
                <FormItem>
                  <FormLabel className="gap-0.5">Select Pricing Scheme <span className="text-destructive">*</span></FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Pricing Scheme" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filteredPricingSchemes.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          No pricing schemes available.
                        </div>
                      ) : (
                        filteredPricingSchemes.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.label}
                            <span className="text-xs text-muted-foreground ml-2">
                              ({s.tiers?.length || 0} tiers)
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {selectedScheme && (
                    <div className="mt-2 p-3 bg-muted/50 rounded-md border">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{selectedScheme.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {selectedScheme.tiers?.length || 0} pricing tier{selectedScheme.tiers?.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        {selectedScheme.tiers && selectedScheme.tiers.length > 0 && (
                          <div className="flex flex-col items-end">
                            <span className="text-xs text-muted-foreground">
                              {selectedScheme.tieringMethod === "WEIGHT_BASED" ? "Weight-based" : "Quantity-based"}
                            </span>
                            {formGetValues("sellUomId") && (
                              <span className="text-xs font-semibold text-primary mt-0.5">
                                Per {uoms.find((u) => u.id === formGetValues("sellUomId"))?.name || "Unit"}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )
            }}
          />
        )}
      </CardContent>
    </Card>
  )
}
